/**
 * What a segment's people did — a sunburst of their acts, under Science site →
 * What sessions did, beside the per-session outcomes.
 *
 * Who is matched on the ACCOUNT's first touch (plus the funnel's own reading of
 * a "did" clause), not on activity rows — so `network = instagram` finds the
 * science site's Instagram readers, most of whom have no app activity at all.
 * Slices are sized by times done, which adds up the rings; people is the
 * second number. See backend `admin/analytics/segmentActivity.ts`.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InfoTip, Select } from '@shared-ui/components';
import { getSegmentActivity, listSegments, describeCriteria } from '../../../api/segmentsApi';
import PathSunburst, { type SunburstVocab } from '../shared/PathSunburst';

const ACTIVITY_VOCAB: SunburstVocab = {
  nameOf: path => (path.length === 0 ? 'Everything they did' : path[path.length - 1]),
  sectionHeader: 'Site',
  childNoun: 'kinds',
  sizeLabel: 'Times',
  sizeUnit: 'times done',
  countLabel: 'People',
  countDetail: 'People',
  hint: where => (
    <>
      Inner ring: {where === 'the site' ? 'which site they did it on' : `what they did on ${where}`}. Outer ring:
      each kind of thing, or which one — which page section, which simulation. Slices are sized by how many
      times it was done; hover one for how many people did it. Click a slice with more under it to open it.
    </>
  ),
};

export default function SegmentActivity({ from, to }: { from: Date; to: Date }) {
  const segments = useQuery({ queryKey: ['segments'], queryFn: listSegments });
  const [segmentId, setSegmentId] = useState('');
  const activity = useQuery({
    queryKey: ['segment-activity', segmentId, from.toISOString(), to.toISOString()],
    queryFn: () => getSegmentActivity(segmentId, from, to),
    enabled: !!segmentId,
  });
  const list = segments.data?.segments ?? [];
  const chosen = list.find(s => s.id === segmentId);

  return (
    <section className="sa-panel">
      <h2>
        What a segment’s people did
        <InfoTip>
          <span className="sa-tip-p">
            Pick a segment to see what its people did in this window, on both sites. A person is in it when
            their account’s first touch matches the segment — so a segment on where people came from finds
            readers who never used the app — and, for a “did” clause, when they did that within its days.
          </span>
          <span className="sa-tip-p">
            Not narrowed by the science site’s filters above: the segment is the filter. Segments are edited
            under Acquisition → Segments.
          </span>
        </InfoTip>
      </h2>
      <div className="seg-activity-pick">
        <Select value={segmentId} onChange={e => setSegmentId(e.target.value)} aria-label="Segment">
          <option value="">Choose a segment…</option>
          {list.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        {chosen && <code className="seg-activity-criteria">{describeCriteria(chosen.criteria)}</code>}
      </div>

      {!segmentId && list.length === 0 && !segments.isLoading && (
        <p className="sa-empty">No segments yet — create one under Acquisition → Segments.</p>
      )}
      {activity.isLoading && segmentId && <p className="sa-empty">Loading…</p>}
      {activity.error && <p className="sa-error">{(activity.error as Error).message}</p>}
      {activity.data && (
        activity.data.people === 0
          ? <p className="sa-empty">Nobody in this segment was active in the window.</p>
          : (
            <>
              <p className="sa-note">
                <strong>{activity.data.people.toLocaleString()}</strong> of the{' '}
                {activity.data.active.toLocaleString()} people active in this window are in the segment.
                {activity.data.behaviourTruncated && ' Its “did” clause named more people than it resolves, so some are missing.'}
              </p>
              <PathSunburst root={activity.data.tree} vocab={ACTIVITY_VOCAB} />
            </>
          )
      )}
    </section>
  );
}
